import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { useDrop } from 'react-dnd';
import { toast } from '@/hooks/ui/use-toast';
import { set, getHours, getMinutes, addMinutes } from 'date-fns';
import { DayShift, Employee, ShiftTemplate } from '@/types';
import { INTERVAL_MINUTES } from './constants';
import { useHistoricalEdit } from '@/context/HistoricalEditContext';
import { Z_INDEX_LAYERS } from '@/lib/scheduling/z-index-layers';
import { useShiftUI } from '@/components/shifts/context/ShiftUIProvider';
import { useSetShiftHover } from '@/components/shifts/context/ShiftHoverProvider';
import { markShiftAsDropped } from './DraggableShift';

/**
 * GridDropOverlay - Single drop zone per day for shift operations
 *
 * This component replaces 48 individual HourCell drop zones with ONE overlay
 * that calculates drop position from coordinates. This reduces:
 * - React-DnD monitor subscriptions from 48 to 1 per day
 * - Event handler attachments by ~97%
 * - Overall component complexity
 */

// Define the structure of a draggable placed shift item
interface PlacedShiftItem {
  shiftId: string;
  title: string;
  startTime: Date;
  endTime: Date;
  isDuplicateMode?: boolean;
  employeeId?: string | null;
  employeeName?: string | null;
  business_id?: string;
  breaks?: any[];
}

interface GridDropOverlayProps {
  dayDate: Date;
  dayIndex: number;
  effectiveDisplayStartTime: Date;
  totalDisplayHours: number;
  hourCellWidthPx: number;
  rowHeightPx: number;
  numRows: number;
  businessId: string | null;
  gridRowStart: number;
  gridRowEnd: number;
  gridColumnEnd: number;
  hideDayColumn?: boolean;
  // Shift operations
  onCreateShift: (businessId: string, startTime: Date, endTime: Date, title: string) => Promise<void>;
  onRepositionShift: (shiftId: string, newStartTime: Date, newEndTime: Date) => Promise<void>;
  onOptimisticPositionUpdate?: (shiftId: string, newStartTime: Date, newEndTime: Date) => void;
  onOptimisticPositionClear?: (shiftId: string) => void;
  // Click handlers
  onTemplateCreationClick?: (cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => void;
  onOpenInlineBuilder?: (cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => void;
  isTemplateCreationInProgress?: boolean;
}

interface CellInfo {
  intervalIndex: number;
  row: number;
  cellTime: Date;
}

const GridDropOverlay: React.FC<GridDropOverlayProps> = ({
  dayDate,
  dayIndex,
  effectiveDisplayStartTime,
  totalDisplayHours,
  hourCellWidthPx,
  rowHeightPx,
  numRows,
  businessId,
  gridRowStart,
  gridRowEnd,
  gridColumnEnd,
  hideDayColumn = false,
  onCreateShift,
  onRepositionShift,
  onOptimisticPositionUpdate,
  onOptimisticPositionClear,
  onTemplateCreationClick,
  onOpenInlineBuilder,
  isTemplateCreationInProgress = false,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [targetCell, setTargetCell] = useState<CellInfo | null>(null);
  const [hoveredCell, setHoveredCell] = useState<CellInfo | null>(null);

  // TimeHeader hover sync
  const setHoveredTimeIntervalIndex = useSetShiftHover();

  // Historical edit protection
  const { isShiftLocked, isHistoricalEditEnabled } = useHistoricalEdit();

  // Quick copy / placing mode from UI context
  const { isPlacingMode, placingShift, cancelPlacingMode } = useShiftUI();

  // Check if this day's date is protected
  const isDayProtected = useMemo(() => {
    return isShiftLocked(dayDate, false);
  }, [isShiftLocked, dayDate, isHistoricalEditEnabled]);

  // Double-tap tracking for mobile
  const lastTapRef = useRef<number>(0);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate the actual cell width from the overlay's own dimensions
  // This ensures alignment with CSS Grid cells regardless of passed-in hourCellWidthPx
  const getActualCellWidth = useCallback((): number => {
    if (!overlayRef.current) return hourCellWidthPx;
    const rect = overlayRef.current.getBoundingClientRect();
    const maxIntervals = Math.ceil(totalDisplayHours * (60 / INTERVAL_MINUTES));
    if (maxIntervals <= 0) return hourCellWidthPx;
    return rect.width / maxIntervals;
  }, [totalDisplayHours, hourCellWidthPx]);

  // Calculate cell info from client coordinates
  const getCellFromCoordinates = useCallback((clientX: number, clientY: number): CellInfo | null => {
    if (!overlayRef.current) return null;

    const rect = overlayRef.current.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;

    // Use actual measured cell width for accurate positioning
    const maxIntervals = Math.ceil(totalDisplayHours * (60 / INTERVAL_MINUTES));
    const actualCellWidth = rect.width / maxIntervals;

    // Clamp to valid range
    const intervalIndex = Math.max(0, Math.min(Math.floor(relativeX / actualCellWidth), maxIntervals - 1));
    const row = Math.max(0, Math.min(Math.floor(relativeY / rowHeightPx), numRows - 1));

    // Calculate the time for this cell
    const minutes = intervalIndex * INTERVAL_MINUTES;
    const cellTime = addMinutes(
      set(dayDate, {
        hours: getHours(effectiveDisplayStartTime),
        minutes: getMinutes(effectiveDisplayStartTime),
        seconds: 0,
        milliseconds: 0
      }),
      minutes
    );

    return { intervalIndex, row, cellTime };
  }, [dayDate, effectiveDisplayStartTime, totalDisplayHours, rowHeightPx, numRows]);

  // Single useDrop for the entire day
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: ['SHIFT_TEMPLATE', 'PLACED_SHIFT'],
    hover: (item, monitor) => {
      const offset = monitor.getClientOffset();
      if (offset) {
        const cell = getCellFromCoordinates(offset.x, offset.y);
        setTargetCell(cell);
      }
    },
    drop: async (item: ShiftTemplate | PlacedShiftItem, monitor) => {
      const offset = monitor.getClientOffset();
      if (!offset) return;

      const cell = getCellFromCoordinates(offset.x, offset.y);
      if (!cell) return;

      const itemType = monitor.getItemType();

      try {
        if (!businessId) {
          toast.error('Cannot perform action: No business ID provided');
          return;
        }

        // Check if this date is protected from historical edits
        if (isDayProtected) {
          toast.error('Cannot create/move shift to a past date', {
            description: 'Enable "Editing Past" mode in the calendar header to modify historical dates.'
          });
          return;
        }

        const newStartTime = cell.cellTime;

        if (itemType === 'SHIFT_TEMPLATE') {
          const templateItem = item as ShiftTemplate;
          const newEndTime = addMinutes(newStartTime, templateItem.duration);
          await onCreateShift(businessId, newStartTime, newEndTime, templateItem.name);
        } else if (itemType === 'PLACED_SHIFT') {
          const placedShiftItem = item as PlacedShiftItem;
          const originalDurationMs = placedShiftItem.endTime.getTime() - placedShiftItem.startTime.getTime();
          const newEndTime = new Date(newStartTime.getTime() + originalDurationMs);

          // Apply optimistic update immediately for smooth visual feedback
          if (onOptimisticPositionUpdate) {
            onOptimisticPositionUpdate(placedShiftItem.shiftId, newStartTime, newEndTime);
          }

          try {
            await onRepositionShift(placedShiftItem.shiftId, newStartTime, newEndTime);
            // Mark shift as dropped to trigger landing animation
            markShiftAsDropped(placedShiftItem.shiftId);
          } catch (error) {
            // Clear optimistic update on error
            if (onOptimisticPositionClear) {
              onOptimisticPositionClear(placedShiftItem.shiftId);
            }
            throw error;
          }
        }

        return { result: 'success' };
      } catch (error) {
        toast.error('Drop operation failed');
        return { result: 'error' };
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop() && !!businessId && !isDayProtected,
    }),
    canDrop: () => {
      return !!businessId && !isDayProtected;
    },
  }), [businessId, dayDate, getCellFromCoordinates, isDayProtected, onCreateShift, onRepositionShift, onOptimisticPositionUpdate, onOptimisticPositionClear]);

  // Clear target on drag leave
  useEffect(() => {
    if (!isOver) {
      setTargetCell(null);
    }
  }, [isOver]);

  // Handle single-click for template creation or shift placement
  const handleClick = useCallback((e: React.MouseEvent) => {
    // Don't stop propagation to allow overlay clicks to work
    if (!isTemplateCreationInProgress) {
      e.stopPropagation();
    }

    const cell = getCellFromCoordinates(e.clientX, e.clientY);
    if (!cell) return;

    // Quick copy / placing mode - create shift from template on single click
    if (isPlacingMode && placingShift && businessId) {
      if (isDayProtected) {
        toast.error('Cannot create shift in the past', {
          description: 'Enable "Editing Past" mode in the calendar header to create shifts on historical dates.'
        });
        return;
      }

      const newStartTime = cell.cellTime;
      const newEndTime = addMinutes(newStartTime, placingShift.duration);

      onCreateShift(businessId, newStartTime, newEndTime, placingShift.title)
        .then(() => cancelPlacingMode())
        .catch(() => {
          toast.error('Failed to place shift');
          cancelPlacingMode();
        });
      return;
    }

    // Template creation behavior
    if (onTemplateCreationClick) {
      onTemplateCreationClick(cell.cellTime, e, cell.row, dayIndex);
    }
  }, [getCellFromCoordinates, isTemplateCreationInProgress, isPlacingMode, placingShift, businessId, isDayProtected, onCreateShift, cancelPlacingMode, onTemplateCreationClick, dayIndex]);

  // Handle double-click to open inline builder
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isTemplateCreationInProgress) return;

    const cell = getCellFromCoordinates(e.clientX, e.clientY);
    if (!cell) return;

    if (isDayProtected) {
      toast.error('Cannot create shift in the past', {
        description: 'Enable "Editing Past" mode in the calendar header to create shifts on historical dates.'
      });
      return;
    }

    if (onOpenInlineBuilder) {
      onOpenInlineBuilder(cell.cellTime, e, cell.row, dayIndex);
    }
  }, [getCellFromCoordinates, isTemplateCreationInProgress, isDayProtected, onOpenInlineBuilder, dayIndex]);

  // Handle touch events for mobile - double tap to open inline builder
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;

    // Clear any existing timeout
    if (touchTimeoutRef.current) {
      clearTimeout(touchTimeoutRef.current);
      touchTimeoutRef.current = null;
    }

    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      e.preventDefault();

      if (isTemplateCreationInProgress) return;

      const touch = e.changedTouches[0];
      const cell = getCellFromCoordinates(touch.clientX, touch.clientY);
      if (!cell) return;

      if (isDayProtected) {
        toast.error('Cannot create shift in the past', {
          description: 'Enable "Editing Past" mode in the calendar header to create shifts on historical dates.'
        });
        return;
      }

      if (onOpenInlineBuilder) {
        const mockEvent = {
          target: e.target,
          clientX: touch.clientX,
          clientY: touch.clientY,
        } as React.MouseEvent;
        onOpenInlineBuilder(cell.cellTime, mockEvent, cell.row, dayIndex);
      }
    } else {
      // Set timeout to reset lastTap after delay
      touchTimeoutRef.current = setTimeout(() => {
        lastTapRef.current = 0;
      }, DOUBLE_TAP_DELAY);
    }
    lastTapRef.current = now;
  }, [getCellFromCoordinates, isTemplateCreationInProgress, isDayProtected, onOpenInlineBuilder, dayIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }
    };
  }, []);

  // Handle mouse move for hover tracking and TimeHeader sync
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const cell = getCellFromCoordinates(e.clientX, e.clientY);
    if (cell) {
      setHoveredCell(cell);
      setHoveredTimeIntervalIndex(cell.intervalIndex);
    }
  }, [getCellFromCoordinates, setHoveredTimeIntervalIndex]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoveredCell(null);
    setHoveredTimeIntervalIndex(null);
  }, [setHoveredTimeIntervalIndex]);

  // Cursor style based on mode
  const cursorStyle = isTemplateCreationInProgress || isPlacingMode ? 'cursor-crosshair' : 'cursor-pointer';

  return (
    <div
      ref={(node) => {
        overlayRef.current = node;
        drop(node);
      }}
      className={`relative ${cursorStyle}`}
      style={{
        gridRow: `${gridRowStart} / ${gridRowEnd}`,
        gridColumnStart: hideDayColumn ? 1 : 2,
        gridColumnEnd: gridColumnEnd,
        height: `${numRows * rowHeightPx}px`, // EXPLICIT height like day template zone
        // Use GRID_INTERACTION (4) for normal state - below shifts so they receive clicks
        // DROP_ZONE (25) is used during drag operations for visual feedback
        zIndex: isOver ? Z_INDEX_LAYERS.DROP_ZONE : Z_INDEX_LAYERS.GRID_INTERACTION,
        pointerEvents: 'auto',
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onTouchEnd={handleTouchEnd}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hover highlight - replaces CSS :hover on individual cells */}
      {/* Uses actual measured cell width for pixel-perfect alignment with CSS Grid */}
      {hoveredCell && !isOver && (() => {
        const actualWidth = getActualCellWidth();
        return (
          <div
            className="absolute bg-gray-300/50 dark:bg-gray-700/50 pointer-events-none transition-[background-color,opacity] duration-75"
            style={{
              left: hoveredCell.intervalIndex * actualWidth,
              top: hoveredCell.row * rowHeightPx,
              width: actualWidth,
              height: rowHeightPx,
            }}
          />
        );
      })()}

      {/* Drop target highlight - positioned at calculated cell */}
      {isOver && targetCell && canDrop && (() => {
        const actualWidth = getActualCellWidth();
        return (
          <div
            className="absolute bg-gray-400/60 dark:bg-gray-500/60 pointer-events-none transition-[background-color,opacity] duration-75"
            style={{
              left: targetCell.intervalIndex * actualWidth,
              top: targetCell.row * rowHeightPx,
              width: actualWidth,
              height: rowHeightPx,
            }}
          />
        );
      })()}
    </div>
  );
};

export default GridDropOverlay;
